import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingDown, Package, ExternalLink } from 'lucide-react';
import { PriceHistoryChart, generateMockPriceHistory } from '@/components/products/PriceHistoryChart';
import sneakersImg from '@/assets/onboarding/sneakers.jpg';
import sambaImg from '@/assets/onboarding/samba.jpg';

const mockPriceData = generateMockPriceHistory(97, 3);

const cardVariants = {
  hidden: { opacity: 0, y: 28, scale: 0.97 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: 0.2 + i * 0.18,
      duration: 0.55,
      type: 'spring' as const,
      damping: 20,
      stiffness: 200,
    },
  }),
};

export function SmartPriceMockup({ isActive = true }: { isActive?: boolean }) {
  const [showChart, setShowChart] = useState(false);
  
  useEffect(() => {
    if (!isActive) { setShowChart(false); return; }
    const timer = setTimeout(() => setShowChart(true), 600);
    return () => clearTimeout(timer);
  }, [isActive]);

  return (
    <div className="absolute inset-0 bg-gradient-to-b from-muted to-background flex items-center justify-center px-5 pt-2 pb-2">
      <div className="w-full max-w-[300px] space-y-2.5">
        {/* Price drop alert */}
        <motion.div
          custom={0}
          initial="hidden"
          animate={isActive ? 'visible' : 'hidden'}
          variants={cardVariants}
          className="bg-card rounded-2xl p-3.5 shadow-lg border border-border/50"
        >
          <div className="flex items-start gap-3">
            <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 bg-muted ring-1 ring-border">
              <img src={sneakersImg} alt="Nike Air Max 90" className="w-full h-full object-cover" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 mb-0.5">
                <TrendingDown className="w-3.5 h-3.5 text-priceDrop" />
                <p className="text-xs font-semibold text-priceDrop">Price Drop!</p>
              </div>
              <p className="text-sm font-semibold text-heading">Nike Air Max 90</p>
              <p className="text-xs text-body mt-0.5">
                Now <span className="font-bold text-priceDrop">$97</span>{' '}
                <span className="line-through text-muted-foreground">$129</span>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Price History Chart - actual component */}
        <motion.div
          custom={1}
          initial="hidden"
          animate={isActive ? 'visible' : 'hidden'}
          variants={cardVariants}
          className="bg-card rounded-2xl p-3.5 shadow-sm border border-border/50 overflow-hidden"
        >
          <div className="h-[104px] [&_h3]:text-xs [&_.grid]:hidden [&_button]:text-[9px] [&_button]:px-2 [&_button]:py-1 [&_.h-40]:h-20 [&_.rounded-full]:hidden">
            {showChart ? (
              <PriceHistoryChart
                data={mockPriceData}
                currentPrice={97}
                currency="USD"
              />
            ) : (
              <div className="h-full" />
            )}
          </div>
        </motion.div>

        {/* Restock alert */}
        <motion.div
          custom={2}
          initial="hidden"
          animate={isActive ? 'visible' : 'hidden'}
          variants={cardVariants}
          className="bg-card rounded-2xl p-3 shadow-sm border border-border/50"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 bg-muted ring-1 ring-border">
              <img src={sambaImg} alt="Adidas Samba" className="w-full h-full object-cover" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 mb-0.5">
                <Package className="w-3.5 h-3.5 text-stockAlert" />
                <p className="text-xs font-semibold text-stockAlert">Back in Stock!</p>
              </div>
              <p className="text-[11px] text-muted-foreground">Adidas Samba - your size is back</p>
            </div>
          </div>
        </motion.div>

        {/* Found cheaper badge */}
        <motion.div
          custom={3}
          initial="hidden"
          animate={isActive ? 'visible' : 'hidden'}
          variants={cardVariants}
          className="bg-priceDrop/5 border border-priceDrop/30 rounded-2xl px-4 py-2.5 flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <ExternalLink className="w-3.5 h-3.5 text-priceDrop" />
            <span className="text-xs font-medium text-heading">Found cheaper at StockX</span>
          </div>
          <span className="text-sm font-bold text-priceDrop">$98</span>
        </motion.div>
      </div>
    </div>
  );
}
